import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, MapPin, Clock, Users, ExternalLink, ArrowLeft, Share2, Ticket, 
  Crown, Check, CalendarRange, ChevronRight, Tag, Instagram, Youtube, Music2,
  Globe, Twitter, AlertCircle, ShieldCheck, Shield, Sparkles, ArrowUpRight, Info, Map
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatEventDate, formatEventDateFull, isEventPast, parseEventDate } from '../lib/dateUtils';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import api, { eventsApi, galleryApi } from '../lib/api';
import OpenCallSlotTile from '../components/OpenCallSlotTile';
import EventBenefitsStrip from '../components/fullbravx/EventBenefitsStrip';
import EventMerchStrip from '../components/EventMerchStrip';
import { usePublicAuth } from '../context/PublicAuthContext';
import { Button } from '../components/ui/button';
import VenueMapViewer from '../components/venue/VenueMapViewer';
import InquiryDialog from '../components/InquiryDialog';
import PublicCommentsSection from '../components/PublicCommentsSection';

// Social icon config
const socialConfig = {
  instagram: { icon: Instagram, color: '#E4405F' },
  youtube: { icon: Youtube, color: '#FF0000' },
  spotify: { icon: Music2, color: '#1DB954' },
  twitter: { icon: Twitter, color: '#1DA1F2' },
  website: { icon: Globe, color: '#6366F1' },
};

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = usePublicAuth();
  const [eventData, setEventData] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [venueData, setVenueData] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [inquiryDialogTier, setInquiryDialogTier] = useState(null);
  const [eventOpenCalls, setEventOpenCalls] = useState([]);
  
  // Capture RRPP referral code from URL
  const rrppRef = searchParams.get('ref');

  const isSubscriber = user?.subscription_active;

  useEffect(() => {
    fetchData();
  }, [id]);

  // Active DJ open calls linked to this event (shown as "slot abierto" in lineup)
  useEffect(() => {
    const evId = eventData?.event?.id;
    if (!evId) return;
    api.get('/dj/open-calls')
      .then(r => setEventOpenCalls((r.data?.open_calls || []).filter(c => c.event_id === evId)))
      .catch(() => {});
  }, [eventData?.event?.id]);

  const fetchData = async () => {
    try {
      // Use the new full endpoint
      const [fullRes, galleryRes] = await Promise.all([
        api.get(`/events/${id}/full`),
        galleryApi.getAll(id).catch(() => ({ data: [] }))
      ]);
      
      setEventData(fullRes.data);
      setGallery(galleryRes.data || []);
      
      // Load venue data if event has venue_id
      if (fullRes.data?.event?.venue_id) {
        try {
          const venueRes = await api.get(`/venues/public/${fullRes.data.event.venue_id}`);
          setVenueData(venueRes.data?.venue || null);
        } catch (venueErr) {
          console.log('Venue not found or not public');
        }
      }
      
      // Pre-select tier from URL or first available
      // Filter: only show visible tiers
      const visibleTiers = (fullRes.data.tiers || []).filter(t => t.is_visible !== false);
      const tierIdFromUrl = searchParams.get('tier');
      if (tierIdFromUrl) {
        const tier = visibleTiers.find(t => t.id === tierIdFromUrl);
        if (tier) setSelectedTier(tier);
      } else if (visibleTiers.length === 1) {
        setSelectedTier(visibleTiers[0]);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Error al cargar el evento');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    // Use the share URL for social media (shows event image in preview)
    // Add timestamp to bust WhatsApp/Facebook cache
    const baseUrl = window.location.origin;
    const timestamp = Date.now();
    const shareUrl = `${baseUrl}/api/s/evento/${event?.slug || eventId}?v=${timestamp}`;
    
    const shareData = {
      title: event?.title,
      text: `${event?.title} - ${event?.location}`,
      url: shareUrl,
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          navigator.clipboard.writeText(shareUrl);
          toast.success('Enlace copiado');
        }
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Enlace copiado');
    }
  };

  // Memoized calculations
  const calculations = useMemo(() => {
    if (!eventData?.event) return null;
    
    const event = eventData.event;
    const serviceFeePercent = event.service_fee || 0;
    
    const calculateServiceFee = (tier) => {
      const basePrice = tier ? tier.price : (event.ticket_price || 0);
      const freeForTier = tier?.free_service_for_subscribers || false;
      if (freeForTier && isSubscriber) return 0;
      return Math.round(basePrice * (serviceFeePercent / 100));
    };
    
    const calculateTotal = (tier) => {
      if (!tier) return (event.ticket_price || 0) + calculateServiceFee(null);
      return tier.price + calculateServiceFee(tier);
    };
    
    return { calculateServiceFee, calculateTotal, serviceFeePercent };
  }, [eventData, isSubscriber]);

  // Google Maps embed URL
  const getMapUrl = (event) => {
    // Helper to check if string is a URL
    const isUrl = (str) => str && (str.startsWith('http://') || str.startsWith('https://'));
    
    // Get the best address to use (location is usually the physical address)
    // address might contain a URL if user put google maps link there incorrectly
    const getPhysicalAddress = () => {
      // Prefer location (physical address) over address if address looks like a URL
      if (event.location && !isUrl(event.location)) {
        return event.location;
      }
      if (event.address && !isUrl(event.address)) {
        return event.address;
      }
      // Last resort: use location even if it might be a URL
      return event.location || event.address;
    };
    
    // If google_maps_url is a valid Google Maps URL, try to extract the query
    if (event.google_maps_url) {
      const googleMapsUrl = event.google_maps_url;
      
      // Short URLs (goo.gl) can't be embedded directly
      if (googleMapsUrl.includes('maps.app.goo.gl') || googleMapsUrl.includes('goo.gl')) {
        const address = getPhysicalAddress();
        if (address && !isUrl(address)) {
          return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(address)}`;
        }
      }
      
      // Try to extract query parameter from full URL
      try {
        const url = new URL(googleMapsUrl);
        const query = url.searchParams.get('q');
        if (query) {
          return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(query)}`;
        }
      } catch (e) {
        // Invalid URL, fall through
      }
    }
    
    // Fallback to physical address
    const address = getPhysicalAddress();
    if (!address || isUrl(address)) return null;
    return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(address)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <Navbar />
        <div className="pt-32 pb-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse space-y-8">
              <div className="h-8 bg-white/5 w-24 rounded" />
              <div className="aspect-[21/9] bg-white/5 rounded-2xl" />
              <div className="h-16 bg-white/5 w-3/4 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!eventData?.event) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <Navbar />
        <div className="pt-32 pb-24 px-4 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-white/5 flex items-center justify-center">
              <Calendar size={32} className="text-white/30" />
            </div>
            <h1 className="text-2xl font-bold mb-4">Evento no encontrado</h1>
            <Link to="/eventos" className="text-[#D4AF37] font-semibold hover:underline">
              Ver todos los eventos
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { event, producer, lineup, tiers: allTiers, subevents, parent_event, co_producers: coProducers = [] } = eventData;
  // Filter tiers: only show visible ones + sort so sold-out are at the bottom
  const tiers = (allTiers || [])
    .filter(t => t.is_visible !== false)
    .sort((a, b) => {
      const soldOutA = (a.is_sold_out || ((a.quantity || 0) - (a.sold || 0)) <= 0 || a.is_active === false) ? 1 : 0;
      const soldOutB = (b.is_sold_out || ((b.quantity || 0) - (b.sold || 0)) <= 0 || b.is_active === false) ? 1 : 0;
      return soldOutA - soldOutB;
    });
  const eventDate = parseEventDate(event.date);
  // Check if event is past - but respect force_active flag
  // Pass event.time to correctly calculate when the event actually ends
  const isPastEvent = event.force_active ? false : isEventPast(event.date, event.time);
  // Consider sold out if no available tiers OR all visible tiers have no stock OR are inactive
  const isSoldOut = tiers.length > 0 && tiers.every(t => (t.quantity - (t.sold || 0)) <= 0 || t.is_active === false);
  const primaryColor = producer?.branding?.primary_color || '#D4AF37';

  // SEO
  const seoDescription = `${event.title} - ${formatEventDateFull(event.date)} en ${event.location}`;

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <SEO 
        title={event.title}
        description={seoDescription}
        image={`${process.env.REACT_APP_BACKEND_URL || ''}/api/og/event/${event.slug || event.id}.png`}
        url={`/evento/${event.slug || event.id}`}
        type="event"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] md:min-h-[85vh] flex flex-col">
        {/* Background - YouTube Video or Flyer Image */}
        <div className="absolute inset-0 overflow-hidden">
          {event.youtube_video_url ? (
            // YouTube Video as Background
            <div className="w-full h-full relative bg-[#030303]">
              {/* Thumbnail as base layer (always visible as fallback) */}
              <img
                src={`https://img.youtube.com/vi/${event.youtube_video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1] || ''}/maxresdefault.jpg`}
                alt={event.title}
                className="absolute inset-0 w-full h-full object-cover z-[1]"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://img.youtube.com/vi/${event.youtube_video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1] || ''}/hqdefault.jpg`;
                }}
              />
              {/* Video iframe on top (will play when autoplay works) */}
              <div className="absolute inset-0 overflow-hidden z-[5]">
                <iframe
                  src={`https://www.youtube.com/embed/${event.youtube_video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1] || ''}?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&playlist=${event.youtube_video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1] || ''}`}
                  title={event.title}
                  className="absolute pointer-events-none"
                  style={{
                    top: '50%',
                    left: '50%',
                    width: '100vw',
                    height: '100vh',
                    minWidth: '177.78vh',
                    minHeight: '56.25vw',
                    transform: 'translate(-50%, -50%)',
                    border: 'none'
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  loading="lazy"
                />
              </div>
              {/* Gradient overlays for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/50 to-[#030303]/20 z-10" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#030303]/70 via-transparent to-transparent z-10" />
              <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#030303] to-transparent z-10" />
            </div>
          ) : (
            // Flyer Image Background
            <>
              <motion.img
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.5 }}
                src={event.image_url || 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920'}
                alt={event.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920';
                }}
              />
              {/* Gradient overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/60 to-[#030303]/30" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#030303]/80 via-transparent to-transparent" />
            </>
          )}
        </div>

        {/* Status Badge */}
        {(isSoldOut || isPastEvent) && (
          <div className="absolute top-24 right-4 md:right-8 z-30">
            <span className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-full ${
              isSoldOut ? 'bg-zinc-800 text-zinc-400' : 'bg-white/10 text-white/70 backdrop-blur-sm'
            }`}>
              {isSoldOut ? 'Agotado' : 'Finalizado'}
            </span>
          </div>
        )}

        {/* Hero Content - z-index 30 to be above video */}
        <div className="relative flex-1 flex flex-col justify-end px-4 md:px-8 pb-8 md:pb-16 pt-24 z-30">
          <div className="max-w-6xl mx-auto w-full">
            {/* Back Button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/50 hover:text-white text-sm mb-8 group"
              data-testid="back-button"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              <span>Volver</span>
            </motion.button>

            {/* Producer Badge - Prominent */}
            {producer && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Link 
                  to={`/productora/${producer.slug}`}
                  className="inline-flex items-center gap-3 px-4 py-2 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 hover:border-white/20 transition-all mb-6 group"
                  data-testid="producer-link"
                >
                  {producer.logo ? (
                    <img 
                      src={producer.logo} 
                      alt={producer.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ background: `${primaryColor}30`, color: primaryColor }}
                    >
                      {producer.name?.charAt(0)}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-white/50 text-xs uppercase tracking-wider">Presenta</span>
                    <span className="font-semibold text-white group-hover:text-[#D4AF37] transition-colors">
                      {producer.name}
                    </span>
                    {producer.is_verified && (
                      <ShieldCheck size={14} className="text-emerald-400" />
                    )}
                  </div>
                  <ArrowUpRight size={14} className="text-white/30 group-hover:text-white/60 transition-colors" />
                </Link>
              </motion.div>
            )}

            {/* Date Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap items-center gap-3 mb-6"
            >
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold"
                style={{ background: primaryColor, color: '#000' }}
              >
                <Calendar size={16} />
                <span className="uppercase tracking-wide">
                  {formatEventDate(event.date, "EEE d MMM")}{event.time ? ` · ${event.time}` : ''}
                </span>
              </div>
              
              {/* Age Restriction */}
              {event.age_restriction_enabled && event.minimum_age && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full text-red-400 text-sm font-semibold">
                  <AlertCircle size={14} />
                  <span>+{event.minimum_age} años</span>
                </div>
              )}
            </motion.div>

            {/* Event Title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.9] mb-6 max-w-4xl"
              data-testid="event-title"
            >
              {event.title}
            </motion.h1>

            {/* Location */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap items-center gap-4 text-white/60"
            >
              <div className="flex items-center gap-2">
                <MapPin size={18} style={{ color: primaryColor }} />
                <span className="text-lg">{event.venue_name || event.location}</span>
              </div>
              {event.time && (
                <div className="flex items-center gap-2">
                  <Clock size={18} style={{ color: primaryColor }} />
                  <span>{event.time}hs</span>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative px-4 md:px-8 py-12 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            
            {/* Left Column - Event Info */}
            <div className="lg:col-span-2 space-y-12 order-last lg:order-first">
              
              {/* About Section */}
              {event.description && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <h2 className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-6">
                    <Info size={14} style={{ color: primaryColor }} />
                    Sobre el evento
                  </h2>
                  <p className="text-lg text-white/70 leading-relaxed whitespace-pre-line">
                    {event.description}
                  </p>
                </motion.div>
              )}

              {/* Custom Event Terms — opens in modal */}
              {event.custom_terms && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  data-testid="event-terms-section"
                >
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    data-testid="event-terms-trigger"
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white/40 hover:text-white/80 transition-colors py-1 px-1 -mx-1"
                  >
                    <Shield size={14} style={{ color: primaryColor }} />
                    Términos y condiciones
                    <ArrowUpRight size={12} className="opacity-60" />
                  </button>
                </motion.div>
              )}

              {/* Lineup Section */}
              {((lineup && lineup.length > 0) || eventOpenCalls.length > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-end justify-between mb-6">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.35em] font-black mb-2 flex items-center gap-2" style={{ color: primaryColor }}>
                        <Users size={12} /> Artistas
                      </p>
                      <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight leading-none">Line-up</h2>
                    </div>
                    {lineup && lineup.length > 0 && (
                      <span 
                        className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border"
                        style={{ color: primaryColor, borderColor: `${primaryColor}44`, background: `${primaryColor}0d` }}
                      >
                        {lineup.length} {lineup.length === 1 ? 'artista' : 'artistas'}
                      </span>
                    )}
                  </div>

                  {lineup && lineup.length > 0 && (() => {
                    // Anchor sorting to the event start time so after-midnight
                    // sets (e.g. 01:00) sort AFTER evening sets (e.g. 23:00).
                    const anchorMatch = /^(\d{1,2}):(\d{2})/.exec(event.time || '');
                    const anchorMin = anchorMatch ? (parseInt(anchorMatch[1], 10) * 60 + parseInt(anchorMatch[2], 10)) : 360;
                    const timeRank = (t) => {
                      const m = /^(\d{1,2}):(\d{2})/.exec(t || '');
                      if (!m) return null;
                      return ((parseInt(m[1], 10) * 60 + parseInt(m[2], 10)) - anchorMin + 1440) % 1440;
                    };
                    const sortedLineup = [...lineup].sort((a, b) => {
                      if (!!a.is_headliner !== !!b.is_headliner) return a.is_headliner ? -1 : 1;
                      const ra = timeRank(a.start_time);
                      const rb = timeRank(b.start_time);
                      if (ra !== null && rb !== null) return ra - rb;
                      if (ra !== null) return -1;
                      if (rb !== null) return 1;
                      return 0;
                    });
                    const hasTimes = sortedLineup.some((a) => a.start_time);
                    return (
                      <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="lineup-list">
                        {sortedLineup.map((artist, idx) => {
                          const djHref = artist.dj_slug
                            ? `/dj/${artist.dj_slug}`
                            : (artist.dj_profile_id ? `/dj/${artist.dj_profile_id}` : null);
                          const isB2bRow = !!(artist.b2b && artist.b2b_artist);
                          const b2bHref = isB2bRow
                            ? (artist.b2b_artist.dj_slug
                                ? `/dj/${artist.b2b_artist.dj_slug}`
                                : (artist.b2b_artist.dj_profile_id ? `/dj/${artist.b2b_artist.dj_profile_id}` : null))
                            : null;
                          const rowInner = (
                            <>
                              {isB2bRow && (artist.image_url || artist.b2b_artist.image_url) ? (
                                <div className="absolute inset-0 pointer-events-none">
                                  {artist.image_url && (
                                    <img
                                      src={artist.image_url}
                                      alt=""
                                      className="absolute inset-0 w-full h-full object-cover object-[center_25%] opacity-45 sm:grayscale sm:group-hover:grayscale-0 transition-all duration-500"
                                      style={{ clipPath: 'polygon(0 0, 53% 0, 48.5% 22%, 53.5% 42%, 47.5% 63%, 52.5% 82%, 49% 100%, 0 100%)' }}
                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                  )}
                                  {artist.b2b_artist.image_url && (
                                    <img
                                      src={artist.b2b_artist.image_url}
                                      alt=""
                                      className="absolute inset-0 w-full h-full object-cover object-[center_25%] opacity-45 sm:grayscale sm:group-hover:grayscale-0 transition-all duration-500"
                                      style={{ clipPath: 'polygon(53% 0, 100% 0, 100% 100%, 49% 100%, 52.5% 82%, 47.5% 63%, 53.5% 42%, 48.5% 22%)' }}
                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                  )}
                                  {/* Grieta: línea quebrada luminosa entre ambos DJs */}
                                  <div
                                    className="absolute inset-0"
                                    style={{
                                      background: primaryColor,
                                      opacity: 0.55,
                                      filter: 'blur(1.5px)',
                                      clipPath: 'polygon(53% 0, 53.9% 0, 49.4% 22%, 54.4% 42%, 48.4% 63%, 53.4% 82%, 49.9% 100%, 49% 100%, 52.5% 82%, 47.5% 63%, 53.5% 42%, 48.5% 22%)'
                                    }}
                                  />
                                  <div
                                    className="absolute inset-0"
                                    style={{
                                      background: primaryColor,
                                      clipPath: 'polygon(53% 0, 53.35% 0, 48.85% 22%, 53.85% 42%, 47.85% 63%, 52.85% 82%, 49.35% 100%, 49% 100%, 52.5% 82%, 47.5% 63%, 53.5% 42%, 48.5% 22%)'
                                    }}
                                  />
                                  <div 
                                    className="absolute inset-0"
                                    style={{ background: 'linear-gradient(to right, rgba(6,6,6,0.94) 0%, rgba(6,6,6,0.6) 28%, rgba(6,6,6,0.82) 50%, rgba(6,6,6,0.6) 72%, rgba(6,6,6,0.94) 100%)' }}
                                  />
                                </div>
                              ) : artist.image_url && (
                                <div className="absolute inset-0 pointer-events-none">
                                  <img 
                                    src={artist.image_url} 
                                    alt=""
                                    className="w-full h-full object-cover object-[center_25%] opacity-35 sm:grayscale sm:group-hover:grayscale-0 transition-all duration-500"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                  <div 
                                    className="absolute inset-0"
                                    style={{ background: 'linear-gradient(to right, rgba(6,6,6,0.97) 0%, rgba(6,6,6,0.88) 40%, rgba(6,6,6,0.55) 75%, rgba(6,6,6,0.75) 100%)' }}
                                  />
                                </div>
                              )}
                              {hasTimes && (
                                <div className="relative w-14 flex-shrink-0 text-right">
                                  {artist.start_time ? (
                                    <>
                                      <span className="block font-mono text-sm font-bold leading-tight" style={{ color: primaryColor }}>
                                        {artist.start_time}
                                      </span>
                                      {artist.end_time && (
                                        <span className="block font-mono text-[10px] text-white/30 leading-tight">{artist.end_time}</span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="font-mono text-sm text-white/20">—</span>
                                  )}
                                </div>
                              )}
                              <div className="relative flex flex-col items-center self-stretch flex-shrink-0">
                                <div className={`w-px flex-1 ${idx === 0 ? 'bg-transparent' : 'bg-white/10'}`} />
                                <div className="w-2 h-2 rounded-full my-1" style={{ background: primaryColor, boxShadow: `0 0 10px ${primaryColor}aa` }} />
                                <div className={`w-px flex-1 ${idx === sortedLineup.length - 1 ? 'bg-transparent' : 'bg-white/10'}`} />
                              </div>
                              {isB2bRow ? (
                                <div className="relative flex-1 min-w-0" data-testid={`b2b-card-${artist.id}`}>
                                  <div className="flex items-center gap-2 sm:gap-3">
                                    {(() => {
                                      const b2bHalf = (name, img, href, pos) => {
                                        const inner = (
                                          <>
                                            <div className="relative w-9 h-9 sm:w-11 sm:h-11 rounded-full overflow-hidden border border-white/15 flex-shrink-0 bg-white/5 group-hover:border-white/30 transition-colors">
                                              {img ? (
                                                <img src={img} alt={name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                              ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-[#0a0a0a]">
                                                  <Music2 size={16} className="text-white/25" />
                                                </div>
                                              )}
                                            </div>
                                            <span className="font-bold text-xs sm:text-base uppercase tracking-wide truncate">{name}</span>
                                            {href && <ArrowUpRight size={11} className="flex-shrink-0 hidden sm:block" style={{ color: primaryColor }} />}
                                          </>
                                        );
                                        const halfCls = "flex items-center gap-2 sm:gap-2.5 flex-1 min-w-0";
                                        return href ? (
                                          <Link to={href} className={`${halfCls} hover:underline underline-offset-4 transition-opacity hover:opacity-90`} style={{ textDecorationColor: primaryColor }} data-testid={`b2b-name-${pos}-${artist.id}`}>
                                            {inner}
                                          </Link>
                                        ) : (
                                          <div className={halfCls} data-testid={`b2b-name-${pos}-${artist.id}`}>{inner}</div>
                                        );
                                      };
                                      return (
                                        <>
                                          {b2bHalf(artist.name, artist.image_url, djHref, 1)}
                                          <span
                                            className="flex-shrink-0 font-black text-[10px] sm:text-sm px-1.5 sm:px-2 py-1 rounded"
                                            style={{ color: primaryColor, background: `${primaryColor}1a`, border: `1px solid ${primaryColor}44` }}
                                          >
                                            B2B
                                          </span>
                                          {b2bHalf(artist.b2b_artist.name, artist.b2b_artist.image_url, b2bHref, 2)}
                                        </>
                                      );
                                    })()}
                                  </div>
                                  <p className="text-[9px] uppercase tracking-[0.25em] font-bold mt-0.5">
                                    {(() => {
                                      const hlLabel = artist.is_headliner ? (artist.headliner_label || 'Headliner') : null;
                                      const showLabel = hlLabel && hlLabel.toLowerCase() !== (artist.role || 'DJ').toLowerCase();
                                      return showLabel ? <span style={{ color: primaryColor }}>{hlLabel} · </span> : null;
                                    })()}
                                    <span className="text-white/35">{artist.role || 'DJ'} · B2B Set{artist.stage ? ` · ${artist.stage}` : ''}</span>
                                  </p>
                                </div>
                              ) : (
                              <>
                              <div className="relative w-11 h-11 rounded-full overflow-hidden border border-white/15 flex-shrink-0 bg-white/5 group-hover:border-white/30 transition-colors">
                                {artist.image_url ? (
                                  <img src={artist.image_url} alt={artist.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Music2 size={16} className="text-white/25" />
                                  </div>
                                )}
                              </div>
                              <div className="relative flex-1 min-w-0">
                                <p className="font-bold text-sm sm:text-base uppercase tracking-wide truncate">{artist.name}</p>
                                <p className="text-[9px] uppercase tracking-[0.25em] font-bold mt-0.5">
                                  {(() => {
                                    const hlLabel = artist.is_headliner ? (artist.headliner_label || 'Headliner') : null;
                                    const showLabel = hlLabel && hlLabel.toLowerCase() !== (artist.role || 'DJ').toLowerCase();
                                    return showLabel ? <span style={{ color: primaryColor }}>{hlLabel} · </span> : null;
                                  })()}
                                  <span className="text-white/35">{artist.role || 'DJ'}{artist.stage ? ` · ${artist.stage}` : ''}</span>
                                </p>
                              </div>
                              </>
                              )}
                              {djHref && !isB2bRow && (
                                <>
                                  <span 
                                    className="relative hidden sm:inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border transition-all group-hover:brightness-125"
                                    style={{ color: primaryColor, borderColor: `${primaryColor}44`, background: `${primaryColor}0d` }}
                                  >
                                    ★ Press kit <ArrowUpRight size={11} />
                                  </span>
                                  <ChevronRight size={16} className="relative text-white/25 sm:hidden flex-shrink-0" />
                                </>
                              )}
                            </>
                          );
                          const rowClasses = "group relative overflow-hidden flex items-center gap-3.5 px-4 sm:px-5 py-3 border-b border-white/5 last:border-b-0 transition-colors";
                          return (djHref && !isB2bRow) ? (
                            <Link key={artist.id} to={djHref} className={`${rowClasses} hover:bg-white/[0.05]`} data-testid={`lineup-tile-${artist.id}`}>
                              {rowInner}
                            </Link>
                          ) : (
                            <div key={artist.id} className={rowClasses} data-testid={`lineup-tile-${artist.id}`}>
                              {rowInner}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {eventOpenCalls.length > 0 && (
                    <div className={`space-y-3 ${lineup && lineup.length > 0 ? 'mt-3' : ''}`}>
                      {eventOpenCalls.map((call, ci) => (
                        <OpenCallSlotTile key={call.id} call={call} delay={ci * 0.05} />
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Points-redeemable benefits linked to this event */}
              <EventBenefitsStrip eventId={event.id} />
              <EventMerchStrip eventId={event.id} />

              {/* Legacy Artists (if no lineup but has artists array) */}
              {(!lineup || lineup.length === 0) && event.artists && event.artists.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <h2 className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-6">
                    <Users size={14} style={{ color: primaryColor }} />
                    Line-up
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {event.artists.map((artist, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm font-semibold hover:border-white/20 transition-colors"
                      >
                        {artist}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Subevents Section */}
              {event.is_parent_event && subevents && subevents.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="p-6 rounded-2xl border border-white/10"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}05, transparent)` }}
                >
                  <h2 className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-6">
                    <CalendarRange size={14} style={{ color: primaryColor }} />
                    Programa ({subevents.length} eventos)
                  </h2>
                  
                  {event.full_pass_price && (
                    <div className="bg-black/30 border border-white/10 rounded-xl p-4 mb-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Tag size={18} style={{ color: primaryColor }} />
                          <div>
                            <p className="font-bold">Pase Completo</p>
                            <p className="text-white/50 text-sm">Acceso a todos los eventos</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold" style={{ color: primaryColor }}>
                            ${event.full_pass_price.toLocaleString('es-AR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {subevents.map((subevent) => (
                      <Link
                        key={subevent.id}
                        to={`/evento/${subevent.id}`}
                        className="flex items-center gap-4 p-4 bg-black/20 border border-white/5 rounded-xl hover:border-white/20 transition-all group"
                      >
                        <div className="w-12 h-12 rounded-lg flex flex-col items-center justify-center text-xs"
                          style={{ background: `${primaryColor}20` }}
                        >
                          <span style={{ color: primaryColor }} className="font-bold">
                            {formatEventDate(subevent.date, 'd')}
                          </span>
                          <span className="text-white/50 uppercase">
                            {formatEventDate(subevent.date, 'MMM')}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate group-hover:text-[#D4AF37] transition-colors">
                            {subevent.title}
                          </p>
                          <p className="text-white/40 text-sm">{subevent.location}</p>
                        </div>
                        <ChevronRight size={16} className="text-white/30 group-hover:text-white/60" />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Parent Event Link */}
              {parent_event && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={`/evento/${parent_event.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm hover:border-white/20 transition-all"
                  >
                    <CalendarRange size={14} style={{ color: primaryColor }} />
                    <span className="text-white/60">Parte de:</span>
                    <span className="font-semibold">{parent_event.title}</span>
                    <ChevronRight size={14} />
                  </Link>
                </motion.div>
              )}

              {/* Location & Map */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-6">
                  <MapPin size={14} style={{ color: primaryColor }} />
                  Ubicación
                </h2>
                
                <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/10">
                  <div className="p-6">
                    <p className="text-xl font-bold mb-1">{event.venue_name || event.location}</p>
                    {event.google_maps_url && (
                      <a 
                        href={event.google_maps_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-white text-sm underline flex items-center gap-1 mb-2"
                      >
                        <ExternalLink size={12} />
                        Ver en Google Maps
                      </a>
                    )}
                    {/* Show address only if it's not a URL */}
                    {event.address && !event.address.startsWith('http') && (
                      <p className="text-white/50">{event.address}</p>
                    )}
                    {/* Show location if different from venue_name and not a URL */}
                    {event.location && !event.location.startsWith('http') && 
                     event.venue_name && event.location !== event.venue_name && (
                      <p className="text-white/40 text-sm mt-1">{event.location}</p>
                    )}
                  </div>
                  
                  {(event.address || event.location || event.google_maps_url) && (
                    <div className="aspect-video relative">
                      <iframe
                        src={getMapUrl(event)}
                        width="100%"
                        height="100%"
                        style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                        allowFullScreen=""
                        loading="lazy"
                        title="Ubicación"
                        className="w-full h-full"
                      />
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Producer Section - Full Info */}
              {producer && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <h2 className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-6">
                    <Sparkles size={14} style={{ color: primaryColor }} />
                    Producido por
                  </h2>
                  
                  <Link 
                    to={`/productora/${producer.slug}`}
                    className="block p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-white/20 transition-all group"
                    data-testid="producer-card"
                  >
                    <div className="flex items-start gap-4">
                      {/* Producer Logo */}
                      {producer.logo ? (
                        <img 
                          src={producer.logo} 
                          alt={producer.name}
                          className="w-16 h-16 rounded-xl object-cover"
                        />
                      ) : (
                        <div 
                          className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-black"
                          style={{ background: `${primaryColor}20`, color: primaryColor }}
                        >
                          {producer.name?.charAt(0)}
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-xl font-bold group-hover:text-[#D4AF37] transition-colors">
                            {producer.name}
                          </h3>
                          {producer.is_verified && (
                            <ShieldCheck size={16} className="text-emerald-400" />
                          )}
                        </div>
                        
                        {(producer.city || producer.province) && (
                          <p className="text-white/40 text-sm flex items-center gap-1 mb-2">
                            <MapPin size={12} />
                            {[producer.city, producer.province].filter(Boolean).join(', ')}
                          </p>
                        )}
                        
                        {producer.description && (
                          <p className="text-white/60 text-sm line-clamp-2 mb-3">
                            {producer.description}
                          </p>
                        )}
                        
                        {/* Social Links */}
                        {producer.social_links && producer.social_links.length > 0 && (
                          <div className="flex items-center gap-2">
                            {producer.social_links.slice(0, 4).map((link, idx) => {
                              const config = socialConfig[link.platform] || socialConfig.website;
                              const Icon = config.icon;
                              return (
                                <span
                                  key={idx}
                                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5"
                                >
                                  <Icon size={14} style={{ color: config.color }} />
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      
                      <ArrowUpRight size={20} className="text-white/30 group-hover:text-white/60 transition-colors flex-shrink-0" />
                    </div>
                  </Link>

                  {/* Co-presented header */}
                  {coProducers.length > 0 && (
                    <div className="mt-4 p-4 bg-white/[0.03] rounded-2xl border border-white/10" data-testid="event-co-producers">
                      <p
                        className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/40 mb-3"
                        style={{ fontFamily: "'Unbounded', sans-serif" }}
                      >
                        Junto a
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        {coProducers.map((cp, i) => (
                          <span key={cp.producer_id} className="contents">
                            {i > 0 && <span className="text-white/30 text-lg">×</span>}
                            <Link
                              to={`/productora/${cp.slug}`}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/25 transition-all"
                              data-testid={`co-producer-${cp.producer_id}`}
                            >
                              {cp.logo ? (
                                <img src={cp.logo} alt={cp.name} className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <span
                                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black"
                                  style={{ background: `${primaryColor}25`, color: primaryColor }}
                                >
                                  {(cp.name || '?').charAt(0)}
                                </span>
                              )}
                              <span className="text-sm font-bold text-white">{cp.name}</span>
                            </Link>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* External Links */}
              {event.external_links && event.external_links.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-4">Enlaces</h2>
                  <div className="flex flex-wrap gap-3">
                    {event.external_links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full text-sm hover:bg-white/10 transition-colors"
                      >
                        <ExternalLink size={14} />
                        {link.label}
                      </a>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Public Q&A / Comments — lives inside the event info column */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <PublicCommentsSection event={event} accentColor={primaryColor} />
              </motion.div>
            </div>

            {/* Right Column - Tickets */}
            <div className="lg:col-span-1 order-first lg:order-last">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="sticky top-24"
              >
                <div className="bg-white/[0.02] backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
                  {/* Header */}
                  <div className="p-5 border-b border-white/10 flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
                      <Ticket size={14} style={{ color: primaryColor }} />
                      Entradas
                    </h3>
                    <button
                      onClick={handleShare}
                      className="p-2 text-white/40 hover:text-white transition-colors rounded-full hover:bg-white/5"
                      data-testid="share-btn"
                    >
                      <Share2 size={16} />
                    </button>
                  </div>
                  
                  {/* Venue Map (if available) */}
                  {event?.venue_map_image_url ? (
                    <div className="p-4 border-b border-white/10">
                      <div className="flex items-center gap-2 mb-3">
                        <Map size={14} style={{ color: primaryColor }} />
                        <span className="text-xs font-bold uppercase tracking-wider">Mapa del Venue</span>
                      </div>
                      <a href={event.venue_map_image_url} target="_blank" rel="noreferrer" data-testid="venue-map-image-link">
                        <img
                          src={event.venue_map_image_url}
                          alt="Mapa del venue"
                          className="w-full rounded-xl border border-white/10 hover:opacity-90 transition-opacity"
                        />
                      </a>
                      <p className="text-[10px] text-zinc-500 mt-2 text-center">Tocá el mapa para verlo completo</p>
                    </div>
                  ) : venueData && (
                    <div className="p-4 border-b border-white/10">
                      <div className="flex items-center gap-2 mb-3">
                        <Map size={14} style={{ color: primaryColor }} />
                        <span className="text-xs font-bold uppercase tracking-wider">Mapa del Venue</span>
                      </div>
                      <VenueMapViewer
                        venue={venueData}
                        tiers={tiers}
                        selectedTierId={selectedTier?.id}
                        onSelectTier={setSelectedTier}
                        showLegend={true}
                      />
                      <p className="text-[10px] text-zinc-500 mt-2 text-center">
                        Selecciona una zona en el mapa o elige abajo
                      </p>
                    </div>
                  )}

                  {/* Ticket Tiers */}
                  <div className="p-4 space-y-3">
                    {tiers.length > 0 ? (
                      tiers.map((tier) => {
                        const tierServiceFee = calculations?.calculateServiceFee(tier) || 0;
                        const tierTotal = calculations?.calculateTotal(tier) || tier.price;
                        const isSelected = selectedTier?.id === tier.id;
                        const tierAvailable = tier.quantity - (tier.sold || 0);
                        const tierSoldOut = tierAvailable <= 0;
                        // Show as "Agotado" if inactive OR actually sold out
                        const isUnavailable = tier.is_active === false || tierSoldOut;
                        const unavailableReason = 'Agotado'; // Always show "Agotado" for unavailable tiers
                        const actualFee = tier.free_service_for_subscribers && isSubscriber 
                          ? Math.round(tier.price * ((calculations?.serviceFeePercent || 0) / 100)) 
                          : 0;

                        return (
                          <motion.button
                            key={tier.id}
                            onClick={() => !isUnavailable && setSelectedTier(tier)}
                            disabled={isUnavailable}
                            className={`relative w-full text-left p-4 rounded-xl transition-all ${
                              isUnavailable 
                                ? 'bg-white/5 opacity-50 cursor-not-allowed' 
                                : isSelected 
                                  ? 'ring-2 ring-[#D4AF37] bg-[#D4AF37]/10' 
                                  : 'bg-white/5 hover:bg-white/10'
                            }`}
                            whileTap={!isUnavailable ? { scale: 0.98 } : {}}
                            data-testid={`tier-${tier.id}`}
                          >
                            {isSelected && (
                              <div className="absolute top-3 right-3">
                                <Check size={16} style={{ color: primaryColor }} />
                              </div>
                            )}
                            
                            <div className="flex items-start justify-between pr-6">
                              <div>
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="font-bold">{tier.name}</span>
                                  {tier.promo_pay_x > 0 && tier.promo_get_y > tier.promo_pay_x && (() => {
                                    const freeUnits = tier.promo_get_y - tier.promo_pay_x;
                                    const promoLabel = `${tier.promo_get_y}x${tier.promo_pay_x}`;
                                    const promoTag = tier.promo_pay_x === 1 && freeUnits === 1
                                      ? 'Llevá un amigo gratis'
                                      : `${freeUnits} de regalo`;
                                    const expired = tier.promo_valid_until && new Date(tier.promo_valid_until) <= new Date();
                                    const remaining = tier.promo_max_redemptions > 0
                                      ? Math.max(0, tier.promo_max_redemptions - (tier.promo_redemptions_count || 0))
                                      : null;
                                    if (expired || remaining === 0) return null;
                                    return (
                                      <>
                                        <span
                                          className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-black uppercase bg-gradient-to-r from-[#D4AF37] to-[#F2D272] text-black rounded-full shadow-[0_0_20px_rgba(212,175,55,0.5)]"
                                          title={`Pagás ${tier.promo_pay_x}, llevás ${tier.promo_get_y}`}
                                          data-testid={`tier-promo-badge-${tier.id}`}
                                        >
                                          🎁 {promoLabel} · {promoTag}
                                        </span>
                                        {remaining !== null && remaining <= 15 && (
                                          <span
                                            className="px-2 py-0.5 text-[10px] font-bold uppercase bg-red-500/15 text-red-400 border border-red-500/40 rounded-full animate-pulse"
                                            data-testid={`tier-promo-remaining-${tier.id}`}
                                          >
                                            Últimos {remaining} {promoLabel}
                                          </span>
                                        )}
                                      </>
                                    );
                                  })()}
                                  {tier.free_service_for_subscribers && (
                                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-[#D4AF37]/20 text-[#D4AF37] rounded">
                                      BRAVX
                                    </span>
                                  )}
                                  {tier.tier_type === 'inquiry' && (
                                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-cyan-500/20 text-cyan-300 rounded flex items-center gap-1">
                                      💬 Consultar
                                    </span>
                                  )}
                                  {tier.current_stage_label && tier.total_stages > 1 && (
                                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-violet-500/20 text-violet-300 rounded">
                                      {tier.current_stage_label} · {tier.current_stage_index + 1}/{tier.total_stages}
                                    </span>
                                  )}
                                </div>
                                {tier.description && (
                                  <p className="text-white/40 text-xs">{tier.description}</p>
                                )}
                              </div>
                              <div className="text-right">
                                {tier.tier_type === 'inquiry' ? (
                                  <p className="font-bold text-sm text-cyan-300">A consultar</p>
                                ) : (
                                  <>
                                    <p className="font-bold text-lg">${tierTotal.toLocaleString()}</p>
                                    {tierServiceFee > 0 && (
                                      <p className="text-white/40 text-[10px]">
                                        inc. ${tierServiceFee.toLocaleString()} servicio
                                      </p>
                                    )}
                                    {actualFee > 0 && (
                                      <p className="text-emerald-400 text-[10px] font-semibold">
                                        Ahorrás ${actualFee.toLocaleString()}
                                      </p>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            
                            {isUnavailable && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl">
                                <span className="px-3 py-1 bg-white/10 rounded-full text-xs font-bold">{unavailableReason}</span>
                              </div>
                            )}
                          </motion.button>
                        );
                      })
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-3xl font-bold mb-1">
                          ${(event.ticket_price || 0).toLocaleString()}
                        </p>
                        {event.service_fee > 0 && (
                          <p className="text-white/40 text-sm">+{event.service_fee}% servicio</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* BRAVX Hint */}
                  {!isSubscriber && tiers.some(t => t.free_service_for_subscribers) && (
                    <Link
                      to="/fullbravx"
                      className="block mx-4 mb-4 p-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl hover:bg-[#D4AF37]/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Crown size={18} className="text-[#D4AF37]" />
                        <span className="text-[#D4AF37] text-sm font-semibold">
                          Con FULL BRAVX el servicio es gratis en tandas seleccionadas
                        </span>
                      </div>
                    </Link>
                  )}

                  {/* Total & Buy Button */}
                  <div className="p-4 border-t border-white/10">
                    {selectedTier && (
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-white/50 text-sm">Total</span>
                        <span className="text-2xl font-bold">
                          ${calculations?.calculateTotal(selectedTier).toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    {!isPastEvent && !isSoldOut ? (
                      (selectedTier || tiers.length === 0) ? (
                        selectedTier?.tier_type === 'inquiry' ? (
                          selectedTier.is_sold_out ? (
                            <button
                              type="button"
                              disabled
                              className="w-full py-4 text-center font-bold uppercase tracking-wider rounded-xl bg-white/5 text-white/40 cursor-not-allowed"
                            >
                              Agotado
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setInquiryDialogTier(selectedTier)}
                              data-testid="inquiry-btn"
                              className="block w-full py-4 text-center font-bold uppercase tracking-wider rounded-xl transition-all hover:opacity-90"
                              style={{ background: primaryColor, color: '#000' }}
                            >
                              {selectedTier.inquiry_label || 'Iniciar consulta'}
                            </button>
                          )
                        ) : (
                          <Link
                            to={`/checkout/${event.id}${selectedTier ? `?tier=${selectedTier.id}` : ''}${rrppRef ? `${selectedTier ? '&' : '?'}ref=${rrppRef}` : ''}`}
                            data-testid="buy-tickets-btn"
                            className="block w-full py-4 text-center font-bold uppercase tracking-wider rounded-xl transition-all"
                            style={{ background: primaryColor, color: '#000' }}
                          >
                            Comprar Entrada
                          </Link>
                        )
                      ) : (
                        <button
                          disabled
                          className="w-full py-4 text-center font-bold uppercase tracking-wider rounded-xl bg-white/10 text-white/40 cursor-not-allowed"
                        >
                          Seleccioná una entrada
                        </button>
                      )
                    ) : (
                      <button
                        disabled
                        className="w-full py-4 text-center font-bold uppercase tracking-wider rounded-xl bg-white/5 text-white/30 cursor-not-allowed"
                      >
                        {isSoldOut ? 'Agotado' : 'Finalizado'}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="px-4 md:px-8 pb-20" data-testid="event-gallery">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-8">Galería</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {gallery.map((item) => (
                <motion.div 
                  key={item.id} 
                  className="aspect-square overflow-hidden rounded-lg"
                  whileHover={{ scale: 0.98 }}
                >
                  <img
                    src={item.url}
                    alt={item.caption || 'Gallery'}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Public Q&A / Comments */}

      <Footer />

      {/* Custom Terms Modal */}
      <AnimatePresence>
        {showTermsModal && event.custom_terms && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowTermsModal(false)}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            data-testid="event-terms-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-2xl bg-zinc-950 border border-white/10 flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <Shield size={18} style={{ color: primaryColor }} />
                  <h3 className="text-base font-bold text-white">Términos y condiciones del evento</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTermsModal(false)}
                  data-testid="event-terms-modal-close"
                  className="text-white/50 hover:text-white text-2xl leading-none px-2"
                  aria-label="Cerrar"
                >
                  ×
                </button>
              </div>
              <div className="px-6 py-5 overflow-y-auto text-sm text-white/80 leading-relaxed whitespace-pre-line">
                {event.custom_terms}
              </div>
              <div className="px-6 py-3 border-t border-white/10 bg-white/[0.02]">
                <p className="text-[11px] text-white/50">
                  Al comprar una entrada, aceptás estos términos específicos del evento.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inquiry chat dialog for "consultar" tickets */}
      <InquiryDialog
        open={!!inquiryDialogTier}
        onClose={() => setInquiryDialogTier(null)}
        event={event}
        tier={inquiryDialogTier}
        accentColor={primaryColor}
      />
    </div>
  );
};

export default EventDetailPage;
